/**
 * @file Cost Type Help Modal Component
 * @description Modal component that displays contextual help information for the
 * currently selected cost type in the Add Cost dialog. Shows description, examples,
 * field explanations, and tips to guide users when entering cost data.
 * Uses ReactDOM.createPortal to render independently from parent modal.
 */

import { Modal, ModalFooter, Button } from '@medsuite/ui-components';
import React from 'react';
import { createPortal } from 'react-dom';

import { costTypeHelpContent } from './costTypeHelpContent';

import type { CostType } from '@medsuite/models';

export interface CostTypeHelpModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** The currently selected cost type */
  costType: CostType | null;
}

/**
 * CostTypeHelpModal Component
 *
 * Displays help information for the selected cost type including:
 * - Description of what the cost type represents
 * - Example use cases
 * - Field explanations
 * - Optional tips for data entry
 */
export const CostTypeHelpModal: React.FC<CostTypeHelpModalProps> = ({
  isOpen,
  onClose,
  costType,
}) => {
  if (!costType) {
    return null;
  }

  const helpContent = costTypeHelpContent[costType];

  if (!helpContent) {
    return null;
  }

  // Use createPortal to render independently from parent modal's DOM tree
  // This prevents the nested dialog issue where closing this modal also closes the parent
  return createPortal(
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Help: ${helpContent.title}`}
      size="lg"
      closeOnOutsideClick={false}
    >
        <div className="space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Description */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Description
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {helpContent.description}
            </p>
          </div>

          {/* Examples */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Example Use Cases
            </h4>
            <ul className="list-disc list-inside space-y-1">
              {helpContent.examples.map((example, index) => (
                <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                  {example}
                </li>
              ))}
            </ul>
          </div>

          {/* Fields */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Fields
            </h4>
            <div className="space-y-3">
              {/* Standard fields */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3">
                <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Standard Fields
                </h5>
                <ul className="space-y-1">
                  {helpContent.fields.standard.map((field, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {field}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Type-specific fields */}
              {helpContent.fields.specific && helpContent.fields.specific.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-3">
                  <h5 className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2">
                    {helpContent.title} Specific Fields
                  </h5>
                  <ul className="space-y-1">
                    {helpContent.fields.specific.map((field, index) => (
                      <li key={index} className="text-sm text-blue-700 dark:text-blue-300">
                        {field}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Tips */}
          {helpContent.tips && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
                Tip
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {helpContent.tips}
              </p>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button variant="primary" onClick={onClose}>
            Got it
          </Button>
        </ModalFooter>
      </Modal>,
    document.body
  );
};

export default CostTypeHelpModal;
